import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Drawer, DrawerContent, DrawerItem, DrawerItemProps, DrawerSelectEvent } from '@progress/kendo-react-layout';
import { gridIcon, globeIcon, gearIcon, logoutIcon, chevronDownIcon, chevronRightIcon, circleIcon, chartLineIcon, clipboardTextIcon, userIcon, gearsIcon, mapMarkerTargetIcon, dataIcon, clockIcon, subreportIcon, clipboardCodeIcon, groupIcon, chartScatterIcon, usersSolidIcon, fileReportIcon, rssBoxIcon } from '@progress/kendo-svg-icons';

import { useEffect, useMemo, useState } from 'react';

import { useMsal } from '@azure/msal-react';

import { useLocalization } from '@progress/kendo-react-intl';

import { Header } from './Header';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Footer } from './Footer';

import { zone } from '../assets/zone'
import { sinottico } from '../assets/sinottico'
import { alarm } from '../assets/alarm'
import { beacon } from '../assets/beacon'

import { SvgIcon } from '@progress/kendo-react-common';

type DrawerMenuItem = {
  separator?: boolean;
  text?: string;
  title?: string;
  selected?: boolean;
  id?: number;
  route?: string;
  svgIcon?: any;
  dataExpanded?: boolean;
  parentId?: number;
  level?: number;
  className?: string;
  visible?: boolean;
};

// opens every ancestor group of the item matching the current path (e.g. when a second-level page is opened directly in a new tab)
const expandAncestorsForPath = (list: DrawerMenuItem[], pathname: string): DrawerMenuItem[] => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const current = list.find((item) => item.route === path);
  if (!current) {
    return list;
  }

  const ancestorIds = new Set<number>();
  let parentId = current.parentId;
  while (parentId !== undefined) {
    ancestorIds.add(parentId);
    parentId = list.find((item) => item.id === parentId)?.parentId;
  }

  return list.map((item) =>
    item.id !== undefined && ancestorIds.has(item.id) ? { ...item, dataExpanded: true } : item
  );
};


const CustomItem = (props: DrawerItemProps) => {
  const { visible, dataExpanded, ...others } = props;

  if ((props as any).separator) {
    return visible === false ? null : (
      <hr className="drawer-item-separator" style={{ margin: '9px 18px', border: 'none', borderTop: '1px solid rgba(29, 63, 72, 0.14)' }} />
    );
  }

  const isSelected = Boolean(props.selected);
  const isParentExpanded = dataExpanded === true;
  const isLevelTwo = props.className?.includes('k-level-2');
  const route = (props as any).route as string | undefined;
  const itemId = (props as any).id as number | undefined;
  const isLink = Boolean(route) && dataExpanded === undefined && itemId !== 10;

  const itemStyle = {
    borderRadius: '14px',
    margin: '0px 10px',
    border: isSelected
      ? '1px solid #6eb6c8'
      : isParentExpanded
      ? '1px solid rgba(208, 113, 53, 0.35)'
      : '1px solid transparent',
    background: isSelected
      ? 'linear-gradient(90deg, rgba(15, 117, 137, 0.28) 0%, rgba(15, 117, 137, 0.1) 100%)'
      : isParentExpanded
      ? 'linear-gradient(90deg, rgba(230, 110, 44, 0.18) 0%, rgba(230, 110, 44, 0.06) 100%)'
      : 'transparent',
    color: isSelected ? '#0b5262' : isLevelTwo ? '#395861' : '#1d3f48',
    boxShadow: isSelected ? 'inset 0 0 0 1px rgba(255, 255, 255, 0.28), 0 4px 12px rgba(15, 107, 131, 0.14)' : 'none',
    transition: 'background-color 180ms ease, border-color 180ms ease, color 180ms ease, box-shadow 180ms ease',
  } as const;

  const iconStyle = {
    color: isSelected ? '#0f7284' : isParentExpanded ? '#c5672e' : '#3d6c77',
  } as const;

  const textStyle = {
    color: isSelected ? '#0b5262' : isLevelTwo ? '#395861' : '#1d3f48',
    fontWeight: isSelected ? 800 : isLevelTwo ? 550 : 700,
    letterSpacing: isSelected ? '0.012em' : '0.004em',
  } as const;

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const isModified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
    if (isModified) {
      e.stopPropagation();
      return;
    }
    e.preventDefault();
  };

  const arrowDir = dataExpanded ? chevronDownIcon : chevronRightIcon;
  return props.visible === false ? null : (
    <DrawerItem {...others} style={itemStyle}>
      <SvgIcon icon={props.svgIcon} style={iconStyle} className="drawer-item-icon" />
      {isLink ? (
        <a
          href={route}
          className="k-item-text drawer-item-text drawer-item-link"
          style={textStyle}
          onClick={handleLinkClick}
        >
          {props.text}
        </a>
      ) : (
        <span className={"k-item-text drawer-item-text"} style={textStyle}>{props.text}</span>
      )}
      {dataExpanded !== undefined && (
        <SvgIcon
          icon={arrowDir}
          className="drawer-item-arrow"
          style={{
            marginLeft: "auto",
            color: isParentExpanded ? '#bf6027' : '#4f7680',
          }}
        />
      )}
    </DrawerItem>
  );
};

export const DrawerContainer = (props: any) => {

  const navigate = useNavigate();
  const location = useLocation();

  const selectedLocation = location.pathname;

  const [drawerExpanded, setDrawerExpanded] = useState(true);

  const { instance } = useMsal();

  const [isSmallerScreen, setIsSmallerScreen] = useState(window.innerWidth < 768);

  const [visible, setVisible] = useState<boolean>(false);

  const localizationService = useLocalization();
  const languageId = localStorage.getItem('languageId');

  const userType = localStorage.getItem('userType')
  const userEmail = localStorage.getItem('email');
  const storedAdmin = localStorage.getItem('admin');
  //const admin = storedAdmin ? JSON.parse(storedAdmin) : false;


  const listMenuItems: DrawerMenuItem[] = useMemo(() => [
      //{ separator: true },
      {
        text: localizationService.toLanguageString('dashboard.title', 'NOT_FOUND'),
        title: localizationService.toLanguageString('dashboard.title', 'NOT_FOUND'),
        selected: true,
        id: 1,
        route: '/home/dashboard',
        svgIcon: gridIcon
      },
      { separator: true, id: 100 },
      {
        text: localizationService.toLanguageString('map.title', 'NOT_FOUND'),
        title: localizationService.toLanguageString('map.title', 'NOT_FOUND'),
        id: 4,
        route: '/home/map',
        svgIcon: globeIcon,
      },
      { separator: true, id: 101 },
      {
        text: localizationService.toLanguageString('zone.title', 'NOT_FOUND'),
        title: localizationService.toLanguageString('zone.title', 'NOT_FOUND'),
        id: 5,
        route: '/home/areas',
        svgIcon: zone,
      },
      {
        text: localizationService.toLanguageString('alarms.title', 'NOT_FOUND'),
        title: localizationService.toLanguageString('alarms.title', 'NOT_FOUND'),
        id: 6,
        route: '/home/alarms',
        svgIcon: alarm,
      },
      { separator: true, id: 102 },
      {
        text: localizationService.toLanguageString('synoptic_devices.title', 'NOT_FOUND'),
        title: localizationService.toLanguageString('synoptic_devices.title', 'NOT_FOUND'),
        id: 2,
        route: '/home/synoptic',
        svgIcon: rssBoxIcon
      },
      {
        text: localizationService.toLanguageString('beacons.menu_title', 'NOT_FOUND'),
        title: localizationService.toLanguageString('beacons.menu_title', 'NOT_FOUND'),
        id: 3,
        route: '/home/beacons',
        svgIcon: beacon
      },
      { separator: true, id: 103 },
      {
        text: localizationService.toLanguageString('configuration.analysis.analysis_title', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.analysis.analysis_title', 'NOT_FOUND'),
        id: 30,
        dataExpanded: false,
        svgIcon: chartLineIcon,
      },
      {
        text: localizationService.toLanguageString('configuration.analysis.heatmap.heatmap_title', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.analysis.heatmap.heatmap_title', 'NOT_FOUND'),
        id: 31,
        parentId: 30,
        level: 2,
        route: '/home/configuration/analysis/heatmap',
        svgIcon: chartScatterIcon,
      },
      {
        text: localizationService.toLanguageString('configuration.analysis.path_history.path_history_title', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.analysis.path_history.path_history_title', 'NOT_FOUND'),
        id: 32,
        parentId: 30,
        level: 2,
        route: '/home/configuration/analysis/path_history',
        svgIcon: chartLineIcon,
      },
      {
        text: localizationService.toLanguageString('configuration.analysis.report.report_title', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.analysis.report.report_title', 'NOT_FOUND'),
        id: 34,
        parentId: 30,
        level: 2,
        route: '/home/configuration/analysis/report',
        svgIcon: fileReportIcon,
      },
      //{
      //  text: 'Synchro',
      //  title: 'Synchro',
      //  id: 8,
      //  //parentId: 9,
      //  route: '/home/configuration/synchro',
      //  svgIcon: synchro,
      //},
      { separator: true, id: 104 },
      {
        text: localizationService.toLanguageString('settings.title', 'NOT_FOUND'),
        title: localizationService.toLanguageString('settings.title', 'NOT_FOUND'),
        id: 7,
        route: '/home/settings',
        svgIcon: gearIcon,
      },
      {
        text: localizationService.toLanguageString('configuration.title', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.title', 'NOT_FOUND'),
        id: 9,
        dataExpanded: false,
        //route: '/home/configuration',
        svgIcon: gearsIcon,
      },
      //{
      //  text: localizationService.toLanguageString('configuration.maps_title', 'NOT_FOUND'),
      //  id: 11,
      //  parentId: 9,
      //  route: '/home/configuration/maps',
      //  svgIcon: globeIcon,
      //},
      //{
      //  text: localizationService.toLanguageString('configuration.beacons_title', 'NOT_FOUND'),
      //  id: 12,
      //  parentId: 9,
      //  route: '/home/configuration/beacons',
      //  svgIcon: beacon,
      //},
      {
        text: localizationService.toLanguageString('configuration.devices_title', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.devices_title', 'NOT_FOUND'),
        id: 13,
        parentId: 9,
        dataExpanded: false,
        //route: '/home/configuration/devices',
        svgIcon: sinottico,
      },
      {
        text: localizationService.toLanguageString('configuration.devices.devices_enable', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.devices.devices_enable', 'NOT_FOUND'),
        id: 17,
        parentId: 13,
        level: 2,
        route: '/home/configuration/devices/devices-enable',
        svgIcon: circleIcon,
      },
      {
        text: localizationService.toLanguageString('configuration.devices.devices_management', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.devices.devices_management', 'NOT_FOUND'),
        id: 18,
        parentId: 13,
        level: 2,
        route: '/home/configuration/devices/devices-management',
        svgIcon: circleIcon,
      },
      {
        text: localizationService.toLanguageString('configuration.devices.devices_binding', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.devices.devices_binding', 'NOT_FOUND'),
        id: 19,
        parentId: 13,
        level: 2,
        route: '/home/configuration/devices/devices-binding',
        svgIcon: circleIcon,
      },
      {
        text: localizationService.toLanguageString('configuration.rules_title', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.rules_title', 'NOT_FOUND'),
        id: 14,
        parentId: 9,
        dataExpanded: false,
        //route: '/home/configuration/rules',
        svgIcon: clipboardTextIcon,
      },
      {
        text: localizationService.toLanguageString('configuration.rules.timeslot', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.rules.timeslot', 'NOT_FOUND'),
        id: 23,
        parentId: 14,
        level: 2,
        route: '/home/configuration/rules/timeslot',
        svgIcon: clockIcon,
      },
      {
        text: localizationService.toLanguageString('configuration.rules.subscription', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.rules.subscription', 'NOT_FOUND'),
        id: 24,
        parentId: 14,
        level: 2,
        route: '/home/configuration/rules/subscription',
        svgIcon: subreportIcon,
      },
      {
        text: localizationService.toLanguageString('configuration.rules.alarmActionHandlers', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.rules.alarmActionHandlers', 'NOT_FOUND'),
        id: 25,
        parentId: 14,
        level: 2,
        route: '/home/configuration/rules/alarm-action-handlers',
        svgIcon: alarm,
      },
      {
        text: localizationService.toLanguageString('configuration.rules.subscriptionAPI', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.rules.subscriptionAPI', 'NOT_FOUND'),
        id: 26,
        parentId: 14,
        level: 2,
        route: '/home/configuration/rules/subscription-api',
        svgIcon: clipboardCodeIcon,
      },      
      {
        text: localizationService.toLanguageString('configuration.locator_title', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.locator_title', 'NOT_FOUND'),
        id: 15,
        parentId: 9,
        dataExpanded: false,
        svgIcon: mapMarkerTargetIcon,
      },
      {
        text: localizationService.toLanguageString('configuration.locator.server', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.locator.server', 'NOT_FOUND'),
        id: 21,
        parentId: 15,
        level: 2,
        route: '/home/configuration/locator/server',
        svgIcon: dataIcon,
      },
      {
        text: localizationService.toLanguageString('configuration.locator.collector', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.locator.collector', 'NOT_FOUND'),
        id: 22,
        parentId: 15,
        level: 2,
        route: '/home/configuration/locator/collector',
        svgIcon: circleIcon,
      },
      {
        text: localizationService.toLanguageString('configuration.registry.title', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.registry.title', 'NOT_FOUND'),
        id: 27,
        parentId: 9,
        dataExpanded: false,
        svgIcon: userIcon,
      },
      {
        text: localizationService.toLanguageString('configuration.registry.groups', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.registry.groups', 'NOT_FOUND'),
        id: 28,
        parentId: 27,
        level: 2,
        route: '/home/configuration/registry/groups',
        svgIcon: groupIcon,
      },
      {
        text: localizationService.toLanguageString('configuration.registry.jobs', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.registry.jobs', 'NOT_FOUND'),
        id: 29,
        parentId: 27,
        level: 2,
        route: '/home/configuration/registry/jobs',
        svgIcon: circleIcon,
      },
      {
        text: localizationService.toLanguageString('configuration.registry.account.title', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.registry.account.title', 'NOT_FOUND'),
        id: 33,
        parentId: 27,
        level: 2,
        route: '/home/configuration/registry/accounts',
        svgIcon: usersSolidIcon,
      },
      {
        text: localizationService.toLanguageString('map.title', 'NOT_FOUND'),
        title: localizationService.toLanguageString('map.title', 'NOT_FOUND'),
        id: 40,
        parentId: 9,
        dataExpanded: false,
        svgIcon: globeIcon,
      },      
      {
        text: localizationService.toLanguageString('configuration.maps.zones', 'NOT_FOUND'),
        title: localizationService.toLanguageString('configuration.maps.zones', 'NOT_FOUND'),
        id: 41,
        parentId: 40,
        level: 2,
        route: '/home/configuration/maps/zones',
        svgIcon: zone,
      },
      {
        text: localizationService.toLanguageString('map.title', 'NOT FOUND'),
        title: localizationService.toLanguageString('map.title', 'NOT FOUND'),
        id: 42,
        parentId: 40,
        level: 2,
        route: '/home/configuration/maps/maps',
        svgIcon: globeIcon,
      },
      //{
      //  text: localizationService.toLanguageString('configuration.privacy_title', 'NOT_FOUND'),
      //  id: 16,
      //  parentId: 9,
      //  route: '/home/configuration/privacy',
      //  svgIcon: lockIcon,
      //},
      //{ separator: true },
      {
        text: localizationService.toLanguageString('logout', 'NOT_FOUND'),
        title: localizationService.toLanguageString('logout', 'NOT_FOUND'),
        id: 10,
        route: '/',
        svgIcon: logoutIcon,
      }
    ], [languageId]);


  const [items, setItems] =   useState<DrawerMenuItem[]>(() => expandAncestorsForPath(listMenuItems, selectedLocation))

  useEffect(() => {
      setItems(expandAncestorsForPath(listMenuItems, selectedLocation))
  }, [listMenuItems])


  const resizeWindow = () => {
    setIsSmallerScreen(window.innerWidth < 768);
  }

  const keysToKeep = [
    `tileState: ${userEmail}`,
    `languageId`
  ];

  const clearLocalStorageExcept = (keysToKeep: string[]) => {
    const localStorageKeys = Object.keys(localStorage);

    localStorageKeys.forEach(key => {
      if (!keysToKeep.includes(key)) {
        localStorage.removeItem(key);
      }
    });
  }

  const closeDialog = () => {
    setVisible(false);
  };

  const executeLogout = () => {
    setVisible(false);
    const isSSO = localStorage.getItem('sso')
    localStorage.removeItem('groupsInfo')
    clearLocalStorageExcept(keysToKeep)

    if (isSSO === 'true') {
      instance.logoutPopup({
        postLogoutRedirectUri: "/",
        mainWindowRedirectUri: "/"
      });
    } else {
      navigate('/');
    }
  };

  const isTokenExpired = () => {
    //const token:any = localStorage.getItem('accessToken') || localStorage.getItem('accessToken');
    const token: any = localStorage.getItem('accessToken');

    if (!token) {
      //localStorage.removeItem('accessToken');
      return true;  // If no token, consider it expired
    }

    try {
      const base64Url = token?.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/'); // Convert to base64 encoding
      const decodedPayload = JSON.parse(atob(base64)); // Decode base64 to JSON
      //const decodedToken:any = jwtDecode(decodedPayload);
      const currentTime = Date.now() / 1000; // Current time in seconds
      return decodedPayload.exp < currentTime;
    } catch (e) {
      return true; // If token is invalid, treat it as expired
    }
  };

  useEffect(() => {
    const isSSO = localStorage.getItem('sso')

    if (isSSO !== 'true' && isTokenExpired()) {
      executeLogout();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', resizeWindow, false)
    resizeWindow();
    return () => {
      window.removeEventListener('resize', resizeWindow, false)
    };
  }, [])

  const handleClick = () => {
    setDrawerExpanded(!drawerExpanded);
  };

  const onSelect = (ev: DrawerSelectEvent) => {
    const selectedIndex = typeof ev?.itemIndex === 'number' ? ev.itemIndex : -1;
    const currentItem = selectedIndex >= 0 ? data[selectedIndex] : ev?.itemTarget?.props;
    if (!currentItem || currentItem.separator) {
      return;
    }

    const isParent = currentItem?.dataExpanded !== undefined;
    const nextExpanded = !currentItem?.dataExpanded;
    const shouldCollapseAllParents = !isParent && Boolean(currentItem?.route);

    const getDescendantIds = (parentId: number): number[] => {
      const directChildren = items
        .filter((item) => item.parentId === parentId)
        .map((item) => item.id)
        .filter((id): id is number => id !== undefined);

      return directChildren.reduce((acc: number[], childId: number) => {
        acc.push(childId, ...getDescendantIds(childId));
        return acc;
      }, []);
    };

    const getAncestorIds = (itemId?: number): number[] => {
      if (typeof itemId !== 'number') {
        return [];
      }

      const ancestors: number[] = [];
      let current = items.find((item) => item.id === itemId);

      while (current?.parentId !== undefined) {
        ancestors.push(current.parentId);
        current = items.find((item) => item.id === current?.parentId);
      }

      return ancestors;
    };

    const parentIdsToKeepExpanded = shouldCollapseAllParents
      ? getAncestorIds(currentItem.id)
      : [];

    const descendantsToCollapse =
      isParent && !nextExpanded && typeof currentItem.id === 'number'
        ? getDescendantIds(currentItem.id)
        : [];

    const newData = items.map((item) => {
      const { selected, dataExpanded: currentExpanded, id, ...others } = item;
      const isCurrentItem = currentItem?.id === id;
      const shouldCollapseDescendant = typeof id === 'number' ? descendantsToCollapse.includes(id) : false;
      const isExpandableItem = currentExpanded !== undefined;

      return {
        selected: isParent || shouldCollapseDescendant ? false : isCurrentItem,
        dataExpanded: !isExpandableItem
          ? undefined
          : shouldCollapseAllParents
          ? typeof id === 'number' && parentIdsToKeepExpanded.includes(id)
          : isCurrentItem && isParent
          ? nextExpanded
          : shouldCollapseDescendant
          ? false
          : currentExpanded,
        id,
        ...others,
      };
    });

    setItems(newData);

    if (currentItem?.id === 10) {
      setVisible(true)
      return;
    }

    if (isParent || !currentItem.route) {
      return;
    }

    if (currentItem.route) {
      if (isSmallerScreen) {
        setDrawerExpanded(false);
      }
      navigate(currentItem.route);
    }
  };

  const data = items.map((item: any) => {
    const { parentId, ...others } = item;

    let hiddenIds: number[] = [];

    switch (userType) {
      case 'UserAdminOnlyLocator':
        hiddenIds = [8, 15, 20];
        if (item.id !== undefined && hiddenIds.includes(item.id)) {
          return { visible: false };
        }
        break;
      case 'StandardOnlyLocator':
        hiddenIds = [8, 15, 17, 18, 20, 26, 33];
        if (hiddenIds.includes(item.id)) {
          return { visible: false };
        }
        break;
      default:
        break;
    }


    if (parentId !== undefined) {
      const parentEl = items.find((parent) => parent.id === parentId);
      return {
        ...others,
        className: `${item.level !== 2 ? 'k-level-1' : 'k-level-2'}`,
        visible: parentEl && parentEl.dataExpanded,
        selected: item.route === selectedLocation
      };
    }
    else {
      return {
        ...others,
        selected: item.route === selectedLocation
      };
    }
  });

  return (
    <>

      <Header onButtonClick={handleClick} changeTheme={props.changeTheme} theme={props.theme} />

      <div className="drawer-layout-shell">

        {visible && (
          <Dialog
            className="shell-logout-dialog"
            title={localizationService.toLanguageString('are_you_sure_to_exit', 'NOT_FOUND')}
            onClose={closeDialog}
            width={430}
          >
            <div className="drawer-dialog-actions">
              <button
                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base logout logout-cancel"
                onClick={closeDialog}
              >
                {localizationService.toLanguageString('no', 'NOT_FOUND')}
              </button>
              <button
                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base logout logout-confirm"
                onClick={executeLogout}
              >
                {localizationService.toLanguageString('yes', 'NOT_FOUND')}
              </button>
            </div>
          </Dialog>
        )}


        <Drawer
          expanded={drawerExpanded}
          //position={'start'}
          mode={isSmallerScreen ? 'overlay' : 'push'}
          mini={isSmallerScreen ? false : true}
          width={320}
          items={data}
          item={CustomItem}
          onSelect={onSelect}
          className={`shell-drawer ${!drawerExpanded && !isSmallerScreen ? 'shell-drawer-collapsed' : ''} ${!drawerExpanded ? 'k-drawer-item expanded' : ''}`}
        >
          <DrawerContent
            className={`drawer-content-shell ${selectedLocation.includes('map') ? 'map-mode' : ''}`}
          >
            {props.children}
            <Outlet context={{ drawerExpanded }} />
          </DrawerContent>
        </Drawer>
      </div>
      <Footer />
    </>
  );
};
